import { useTranslation } from "react-i18next";
import {
  IconBell,
  IconFileText,
  IconLock,
  IconMessage2,
  IconRoute,
  IconShare,
  IconSparkles,
  IconStethoscope,
  IconUser,
  IconWorld,
  IconX,
} from "@tabler/icons-react";
import NavRoomsIcon from "../../../../assets/icons/nav/nav-rooms.svg?react";
import { useCurrentUser } from "../../../../domains/user/hooks/useCurrentUser";
import { useLogOutUser } from "../../../../domains/user/hooks/useLogOutUser";
import { useApiQuery } from "../../../../services/api";
import { getItemTranslation } from "../../../../domains/app/utils/getItemTranslation";
import { useActiveLang } from "../../../../domains/app/hooks/useActiveLang";
import ProfilePicture from "../../../ProfilePicture";
import { Avatar } from "../../../ui/Avatar";
import { CALENDLY_URL } from "../../../ui/FeedbackCard";
import {
  closeSettings,
  pushSettingsView,
} from "../../../../domains/app/stores/settingsSheetStore";
import { resetAvatarPicker } from "../../../../domains/user/stores/avatarPickerStore";
import { usePracticeArm } from "../../../../domains/practice/hooks/usePracticeArm";
import { SettingsRow } from "../SettingsRow";
import { SettingsSectionLabel } from "../SettingsSectionLabel";
import { LANGUAGE_LABELS } from "./LanguageView";
import { openExternalUrl } from "../../../../tools/open-external-url";
import { useWizardRestart } from "../../../../features/juiscigpt-wizard/useWizardRestart";

export function RootView(): React.JSX.Element {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const { activeLang } = useActiveLang();
  const logout = useLogOutUser();
  const restartTour = useWizardRestart();
  // Which of the two relevance questions this account is asked, and nothing else
  // here reads it. Without this the practice row stood for every account: a dead
  // end for the ~93 Specialties with no catalogue, and worse where one HAD been
  // imported without the Specialty being targeted — the editor worked, and saved
  // practices that changed nobody's feed.
  const practiceArm = usePracticeArm();
  // "Partner sharing" (Figma O4). Its subtitle says whether the global switch
  // is on.
  const sharingQuery = useApiQuery(
    "/webapp/private/users/me/partner-sharing",
    currentUser !== null ? { query: { language: activeLang } } : false
  );

  // Certification eligibility — only query when the user could still request it
  // (mirrors RequestCertificationPage's own guard).
  const certifiedQuery = useApiQuery(
    "/webapp/private/lemon-certified/request",
    currentUser !== null && currentUser.lemonCertified !== true
      ? { durable: true }
      : false
  );

  if (currentUser === null) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex shrink-0 justify-end px-5 pt-1">
          <button
            type="button"
            onClick={closeSettings}
            aria-label={t("Close")}
            className="flex size-6 items-center justify-center text-ds-black"
          >
            <IconX size={24} stroke={2} />
          </button>
        </div>
      </div>
    );
  }

  const mainSpecialty = currentUser.mainSpecialtyModel;
  const specialtySubtitle =
    mainSpecialty === null ? null : getItemTranslation(mainSpecialty);
  const languageSubtitle = LANGUAGE_LABELS[activeLang].native;

  const canVerify =
    currentUser.lemonCertified !== true &&
    certifiedQuery.state === "success" &&
    // `null` is "no request on file". A request that exists always carries a
    // status, so the `status === undefined` half of this was never reachable.
    certifiedQuery.body === null;

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 justify-end px-5 pt-1">
        <button
          type="button"
          onClick={closeSettings}
          aria-label={t("Close")}
          className="flex size-6 items-center justify-center text-ds-black"
        >
          <IconX size={24} stroke={2} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scroll-fog-y px-5 pb-12">
        <h2 className="text-heading-display font-semibold text-ds-black">
          {t("Settings")}
        </h2>

        <button
          type="button"
          onClick={() => {
            pushSettingsView({ view: "editProfile" });
          }}
          className="mt-5 flex w-full flex-col items-center gap-3"
        >
          <ProfilePicture user={currentUser} height={96} badge={null} />
          <span className="flex flex-col items-center gap-0.5">
            <span className="text-ui-highlight font-medium text-ds-black">
              {currentUser.firstname} {currentUser.lastname}
            </span>
            <span className="text-ui-note text-ds-slate-500">
              {t("Edit profile")}
            </span>
          </span>
        </button>

        {canVerify ? (
          <button
            type="button"
            onClick={() => {
              resetAvatarPicker();
              pushSettingsView({ view: "certification" });
            }}
            className="mt-6 flex w-full items-center gap-3 rounded-2xl bg-ds-slate-50 p-3 text-left"
          >
            <Avatar
              variant="icon"
              size="md"
              icon={<IconUser size={20} stroke={1.5} />}
              badge={null}
            />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-ui-body font-semibold text-ds-black">
                {t("Verify my account")}
              </span>
              <span className="text-ui-caption text-ds-slate-700">
                {t("Be part of the community, verify your account")}
              </span>
            </span>
          </button>
        ) : null}

        <div className="mt-6 flex flex-col gap-1">
          <SettingsSectionLabel>{t("ACCOUNT")}</SettingsSectionLabel>
          <div className="flex flex-col divide-y divide-ds-slate-100">
            <SettingsRow
              icon={<IconUser size={20} stroke={1.5} />}
              title={t("Account")}
              subtitle={t("Email")}
              right={{ kind: "chevron" }}
              tone="default"
              onClick={() => {
                pushSettingsView({ view: "account" });
              }}
            />
            <SettingsRow
              icon={<IconStethoscope size={20} stroke={1.5} />}
              title={t("My main specialty")}
              subtitle={specialtySubtitle}
              right={{ kind: "chevron" }}
              tone="default"
              onClick={() => {
                pushSettingsView({ view: "mainSpecialty" });
              }}
            />
            <SettingsRow
              icon={<IconWorld size={20} stroke={1.5} />}
              title={t("Language")}
              subtitle={languageSubtitle}
              right={{ kind: "chevron" }}
              tone="default"
              onClick={() => {
                pushSettingsView({ view: "language" });
              }}
            />
            <SettingsRow
              icon={<IconBell size={20} stroke={1.5} />}
              title={t("Notifications")}
              subtitle={null}
              right={{ kind: "chevron" }}
              tone="default"
              onClick={() => {
                pushSettingsView({ view: "notifications" });
              }}
            />
            <SettingsRow
              icon={<IconShare size={20} stroke={1.5} />}
              title={t("Partner sharing")}
              subtitle={
                sharingQuery.state === "success"
                  ? sharingQuery.body.sharing
                    ? t("Sharing on")
                    : t("Sharing off")
                  : null
              }
              right={{ kind: "chevron" }}
              tone="default"
              onClick={() => {
                pushSettingsView({ view: "partnerSharing" });
              }}
            />
            {/* ONE of the two, never both: in the arm the practices ARE what
                the feed is relevant to, and a topics screen that still saved
                would offer a second answer to a question nothing reads any more.
                `pending` keeps the topics row — an unresolved flag renders what
                the app rendered before this arm, here as everywhere else. */}
            {practiceArm === "in" ? (
              <SettingsRow
                icon={<IconStethoscope size={20} stroke={1.5} />}
                title={t("My practice")}
                subtitle={t("What we show you publications for")}
                right={{ kind: "chevron" }}
                tone="default"
                onClick={() => {
                  pushSettingsView({ view: "practice" });
                }}
              />
            ) : (
              <SettingsRow
                icon={<IconSparkles size={20} stroke={1.5} />}
                title={t("Interests")}
                subtitle={t("Choose the topics for your feed")}
                right={{ kind: "chevron" }}
                tone="default"
                onClick={() => {
                  pushSettingsView({ view: "interests" });
                }}
              />
            )}
            {currentUser.rooms.length > 0 ? (
              <SettingsRow
                icon={<NavRoomsIcon className="size-5" />}
                title={t("My rooms")}
                subtitle={t("Manage rooms")}
                right={{ kind: "chevron" }}
                tone="default"
                onClick={() => {
                  pushSettingsView({ view: "rooms" });
                }}
              />
            ) : null}
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-1">
          <SettingsSectionLabel>{t("ABOUT")}</SettingsSectionLabel>
          <div className="flex flex-col divide-y divide-ds-slate-100">
            <SettingsRow
              icon={<IconFileText size={20} stroke={1.5} />}
              title={t("Terms of use")}
              subtitle={null}
              right={{ kind: "chevron" }}
              tone="default"
              onClick={() => {
                pushSettingsView({ view: "terms", tab: "juisci" });
              }}
            />
            <SettingsRow
              icon={<IconLock size={20} stroke={1.5} />}
              title={t("Privacy policy")}
              subtitle={null}
              right={{ kind: "chevron" }}
              tone="default"
              onClick={() => {
                pushSettingsView({ view: "privacy", tab: "juisci" });
              }}
            />
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-1">
          <SettingsSectionLabel>{t("HELP")}</SettingsSectionLabel>
          <div className="flex flex-col divide-y divide-ds-slate-100">
            {restartTour === null ? null : (
              <SettingsRow
                icon={<IconRoute size={20} stroke={1.5} />}
                title={t("Take the guided tour again")}
                subtitle={null}
                right={{ kind: "chevron" }}
                tone="default"
                onClick={() => {
                  closeSettings();
                  void restartTour();
                }}
              />
            )}
            <SettingsRow
              icon={<IconMessage2 size={20} stroke={1.5} />}
              title={t("Give us feedback")}
              subtitle={null}
              right={{ kind: "external" }}
              tone="default"
              onClick={() => {
                openExternalUrl(CALENDLY_URL);
              }}
            />
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            closeSettings();
            void logout();
          }}
          className="mt-8 flex w-full items-center justify-center py-3 text-ui-body font-semibold text-ds-clementine-600"
        >
          {t("Log Out")}
        </button>
      </div>
    </div>
  );
}
